"use client";

/**
 * Renders a single custom field input for activity signup
 * Supports text, textarea, checkbox, and select field types
 */

import type React from "react";
import type { ActivityCustomFieldConfig } from "../../_logic/types/db-types";

interface CustomFieldInputProps {
  field: ActivityCustomFieldConfig;
  value: string | boolean | undefined;
  onChange: (value: string | boolean) => void;
}

/** Renders a single custom field input */
export function CustomFieldInput({ field, value, onChange }: CustomFieldInputProps): React.ReactElement {
  const id = `custom-${field.id}`;

  if (field.type === "checkbox") {
    return (
      <div className="flex items-center gap-2">
        <input
          id={id}
          type="checkbox"
          checked={!!value}
          onChange={(e) => onChange(e.target.checked)}
          className="h-4 w-4 rounded border-gray-300"
        />
        <label htmlFor={id} className="text-sm font-medium text-gray-700">
          {field.label}
          {field.required && <span className="text-red-500"> *</span>}
        </label>
      </div>
    );
  }

  if (field.type === "textarea") {
    return (
      <div className="flex flex-col gap-1">
        <label htmlFor={id} className="text-sm font-medium text-gray-700">
          {field.label}
          {field.required && <span className="text-red-500"> *</span>}
        </label>
        <textarea
          id={id}
          value={(value as string) ?? ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder={field.placeholder}
          required={field.required}
          rows={3}
          className="rounded-lg border border-gray-200 px-3 py-2 text-base font-medium text-gray-900 outline-none transition-colors placeholder:text-gray-400 focus:border-indigo-300 focus:ring-1 focus:ring-indigo-300 sm:text-sm"
        />
      </div>
    );
  }

  if (field.type === "select" && field.options?.length) {
    return (
      <div className="flex flex-col gap-1">
        <label htmlFor={id} className="text-sm font-medium text-gray-700">
          {field.label}
          {field.required && <span className="text-red-500"> *</span>}
        </label>
        <select
          id={id}
          value={(value as string) ?? ""}
          onChange={(e) => onChange(e.target.value)}
          required={field.required}
          className="rounded-lg border border-gray-200 px-3 py-2 text-base font-medium text-gray-900 outline-none transition-colors focus:border-indigo-300 focus:ring-1 focus:ring-indigo-300 sm:text-sm"
        >
          <option value="">{field.placeholder || "Select..."}</option>
          {field.options.map((opt) => (
            <option key={opt} value={opt}>
              {opt}
            </option>
          ))}
        </select>
      </div>
    );
  }

  // Default: text input
  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={id} className="text-sm font-medium text-gray-700">
        {field.label}
        {field.required && <span className="text-red-500"> *</span>}
      </label>
      <input
        id={id}
        type="text"
        value={(value as string) ?? ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={field.placeholder}
        required={field.required}
        className="rounded-lg border border-gray-200 px-3 py-2 text-base font-medium text-gray-900 outline-none transition-colors placeholder:text-gray-400 focus:border-indigo-300 focus:ring-1 focus:ring-indigo-300 sm:text-sm"
      />
    </div>
  );
}
